<script>
  import { trimHtml, insertIntoLastElement } from '@/utilities/strings'

  /** Rich Text sub-block used in the Text Column block content area */

  export default {
    name: 'BuilderTextBlockRichText',

    props: {
      /** The HTML string to display */
      text: {
        type: String,
        required: true,
      },
      /** The text size */
      size: {
        type: String,
        required: false,
        default: 'normal',
        validator: (propValue) => {
          const validTypes = ['normal', 'large']
          return validTypes.indexOf(propValue) > -1
        },
      },
      /** Whether or not to make this block expand/contract */
      collapsable: {
        type: Boolean,
        required: false,
      },
      /** The number of characters to truncate at when the component is set in 'collapsable' mode */
      truncateLimit: {
        type: Number,
        required: false,
        default: 180,
      },
    },

    data() {
      return {
        isOpen: false,
      }
    },

    computed: {
      /** Generates tailwind class for large and normal text sizes */
      contentStyles: function () {
        return this.size === 'large' ? 'text-xl' : 'text-base'
      },
      /** The text content trimmed down for the summary */
      trimmedContent: function () {
        return trimHtml(this.text, { limit: this.truncateLimit })
      },
      /** The full HTML text with indicator toggle added inline */
      fullText: function () {
        if (this.hasMore) {
          const addedContent =
            '<span class="relative inline-block ml-1 text-black | content-clamp-toggle"> - </span>'
          return insertIntoLastElement(this.text, addedContent)
        } else {
          return this.fullContent
        }
      },
      /** The trimmed HTML text with indicator toggle added inline */
      trimmedText: function () {
        const addedContent =
          '<span class="relative inline-block ml-1 text-black | content-clamp-toggle"> + </span>'
        return insertIntoLastElement(this.trimmedContent.html, addedContent)
      },
      /** Returns a boolean of true if there is more text than the trimmed summary */
      hasMore: function () {
        return this.trimmedContent.more
      },
      /** Aria label for toggle button */
      toggleLabel: function () {
        return this.isOpen ? 'Collapse text' : 'Expand text'
      },
    },

    mounted() {
      /** On mount prevent any links in the content areas from propagating a click */
      if (this.collapsable) {
        const contentLinks = this.$refs.content.querySelectorAll('a')
        for (let i = 0; i < contentLinks.length; i++) {
          contentLinks[i].addEventListener('click', (event) => {
            event.stopPropagation()
          })
        }
      }
    },

    methods: {
      /** Toggles the summary and full text */
      toggleClamp() {
        this.isOpen = !this.isOpen
      },
    },
  }
</script>

<template>
  <div v-if="text" class="rich-text">
    <div v-if="collapsable">
      <button
        v-show="hasMore"
        ref="toggle"
        type="button"
        class="text-left | focus:outline-none || content-clamp"
        :aria-label="toggleLabel"
        @click="toggleClamp"
      >
        <div
          v-show="!isOpen"
          ref="content"
          class="text-black || rich-text"
          :class="contentStyles"
          v-html="trimmedText"
        ></div>
        <div
          v-show="isOpen"
          ref="content"
          class="text-gray-600 || rich-text"
          :class="contentStyles"
          v-html="fullText"
        ></div>
      </button>
      <div v-show="!hasMore" class="text-left">
        <div class="text-black || rich-text" :class="contentStyles" v-html="fullText"></div>
      </div>
    </div>
    <div v-else class="text-black || rich-text" :class="contentStyles" v-html="text"></div>
  </div>
</template>

<style scoped lang="postcss">
  /* purgecss start ignore */
  .content-clamp {
    &:hover,
    &:focus {
      >>> .content-clamp-toggle {
        @apply underline;
      }
    }
  }
  /* purgecss end ignore */
</style>
